import React, { useContext, useState } from "react";
import { AppContext } from "../direct-router";
import { useForm } from "react-hook-form";
import { USER_STATE } from "../../../types/generic_types";
import { OnboardingZero } from "../../../types/onboarding_types";
import API_CLIENT from "../../../utility/api-client";
import { GeistHookFormInput, GeistHookFormSalutationSelect, GeistHookFormTitleSelect } from "../investment-process-v2/components/geist-form-inputs";
import { Grid, Text, Button, Spacer, useTheme, Divider } from "@geist-ui/core";
import { BrandLogo } from "../../../components/brand-logo";
import Cookies from "js-cookie";

export default function OnboardingSplit() {
    const app_context = useContext(AppContext);
    const theme = useTheme();
    const [loading, setLoading] = useState(false);

    const {
        control,
        formState: { isValid },
        handleSubmit,
    } = useForm<any>({ mode: "onChange" });

    React.useEffect(() => {
        // Identify User in HubSpot after social login
        if (app_context.userEmail && (window as any)._hsq) {
            (window as any)._hsq.push(["identify", {
                email: app_context.userEmail
            }]);
            (window as any)._hsq.push(['trackPageView']);
        }
    }, [app_context.userEmail]);

    const submitToHubSpot = async (data: any) => {
        const hubspotCookie = Cookies.get('hubspotutk');
        const context = {
            hutk: hubspotCookie,
            pageUri: window.location.href,
            pageName: "Onboarding Completion Page"
        };

        const fields = [
            { name: "email", value: app_context.userEmail },
            { name: "firstname", value: data.firstName },
            { name: "lastname", value: data.lastName },
            { name: "phone", value: data.phoneNumber },
        ];

        try {
            const portalId = process.env.REACT_APP_HUBSPOT_ID || "8989451";
            const formId = "YOUR_SIGNUP_FORM_ID"; // Use same form or separate one
            await fetch(`https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ fields, context })
            });
        } catch (e) {
            console.error("HubSpot completion submission failed", e);
        }
    };

    const submit = (data: OnboardingZero) => {
        setLoading(true);
        
        // Parallel HubSpot Submission
        submitToHubSpot(data);

        let affiliate_program = "";
        let cookie = Cookies.get("iv-affiliate");
        if (cookie !== undefined) {
            affiliate_program = cookie;
        }
        data.affiliate_program = affiliate_program;
        data.termsAccepted = true; 

        API_CLIENT.putOnboardingZero(
            app_context.token,
            data,
            (user) => {
                app_context.setUserState(USER_STATE.NONE);
                app_context.updateUser(user);
                setLoading(false);
            },
            (error) => {
                console.log("error", error);
                setLoading(false);
            }
        );
    };

    return (
        <div className="fixed inset-0 z-[1000] flex bg-white overflow-hidden" style={{ fontFamily: "'Nunito Sans', sans-serif" }}>
            {/* Left Side: Form (White) */}
            <div className="w-full md:w-1/2 h-full flex flex-col justify-center items-center p-6 md:p-16 bg-white relative overflow-hidden">
                {/* Massive Background Logo Bottom Left (Visibility Fix) */}
                <div style={{ 
                    position: 'absolute', 
                    bottom: '-30px', 
                    left: '-20px', 
                    zIndex: 1,
                    pointerEvents: 'none',
                    userSelect: 'none',
                }}>
                    <Text h1 style={{ 
                        fontSize: 'min(35vw, 320px)', 
                        fontWeight: 900, 
                        color: 'rgba(0,0,0,0.08)', 
                        margin: 0, 
                        letterSpacing: '-0.05em', 
                        lineHeight: 0.8,
                        fontFamily: "'Nunito Sans', sans-serif"
                    }}>
                        inventure.
                    </Text>
                </div>

                <div className="w-full max-w-md relative z-20">
                    <div className="mb-12">
                        <BrandLogo fontSize="24px" style={{ marginBottom: '64px' }} />
                        
                        <Text p style={{ color: '#888', fontSize: '14px', margin: 0, fontWeight: 600 }}>
                            Willkommen bei inVenture
                        </Text>
                        <Text h1 style={{ 
                            margin: '12px 0 0 0', 
                            fontSize: '48px',
                            lineHeight: 1.05,
                            letterSpacing: '-0.04em', 
                            fontWeight: 800,
                            fontFamily: "'Nunito Sans', sans-serif",
                            color: '#111'
                        }}>
                            Account <br /> <i style={{ fontStyle: 'italic' }}>vervollständigen</i>
                        </Text>
                    </div>

                    <form onSubmit={handleSubmit(submit)} className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
                        <div className="col-span-1">
                            <GeistHookFormSalutationSelect
                                control={control}
                                name="salutation"
                                label="Anrede*"
                                required
                            />
                        </div>
                        <div className="col-span-1">
                            <GeistHookFormTitleSelect
                                control={control}
                                name="title"
                                label="Titel"
                            />
                        </div>
                        
                        <div className="col-span-2">
                            <GeistHookFormInput
                                control={control}
                                name="firstName"
                                label="Vorname*"
                                required
                            />
                        </div>
                        
                        <div className="col-span-2">
                            <GeistHookFormInput
                                control={control}
                                name="lastName"
                                label="Nachname*"
                                required
                            />
                        </div>
                        
                        <div className="col-span-2">
                            <GeistHookFormInput
                                control={control}
                                name="phoneNumber"
                                label="Telefonnummer*"
                                required
                                placeholder="+49 123 456789"
                            />
                        </div>

                        <div className="col-span-2 mt-4">
                            <Button 
                                width="100%" 
                                type="secondary" 
                                htmlType="submit" 
                                loading={loading}
                                style={{ 
                                    borderRadius: '100px', 
                                    fontWeight: 800, 
                                    fontSize: '16px',
                                    height: '60px',
                                    background: '#000',
                                    color: '#fff',
                                    border: 'none'
                                }}
                            >
                                Weiter
                            </Button>
                        </div>
                    </form>

                    <Text small style={{ color: '#999', marginTop: '24px', textAlign: 'center', display: 'block', fontSize: '12px', fontWeight: 500 }}>
                        Ihre Daten werden sicher und verschlüsselt verarbeitet.
                    </Text>
                </div>
            </div>

            {/* Right Side: Clean Typography (Black Background) */}
            <div className="hidden md:flex w-1/2 h-full bg-[#000] flex-col justify-center px-16 md:px-24 relative overflow-hidden">
                 {/* Radial Background Accent */}
                 <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '100%',
                    height: '100%',
                    background: 'radial-gradient(circle, rgba(255,255,255,0.03) 1px, transparent 1px)',
                    backgroundSize: '32px 32px',
                    zIndex: 0,
                    pointerEvents: 'none'
                }} />

                  <div className="max-w-xl relative z-10 text-white">
                    <Text small style={{ color: 'rgba(255,255,255,0.4)', display: 'block', fontWeight: 800, textTransform: 'uppercase', fontSize: '10px', letterSpacing: '0.2em', marginBottom: '20px' }}>Private Markets</Text>
                    <Text h1 style={{ fontSize: '56px', color: '#fff', fontWeight: 900, letterSpacing: '-0.05em', lineHeight: 0.95, margin: 0 }}>
                        Institutioneller <br/><i style={{ fontStyle: 'italic', fontWeight: 400 }}>Zugang.</i>
                    </Text>
                    <Text span style={{ fontSize: '22px', color: 'rgba(255,255,255,0.7)', display: 'block', marginTop: '24px' }}>
                        Über führende Venture Capital Fonds.
                    </Text>
                 </div>
            </div>
        </div>
    );
}
